import React, { useEffect, useId, useRef, useState } from 'react';
import { MODE_DRAWS, resolvePreset } from 'thinking-orbs';
import type { OrbState } from 'thinking-orbs';
import { useReducedMotion } from '../../animations/useReducedMotion';
import { ArrowRight } from '../../design/icons';
import { useTheme } from '../../theme/ThemeContext';

interface PathOrbProps {
    state: OrbState;
    source: string;
    target: string;
}

const DURATION = 4800;
const FRAME_INTERVAL = 1000 / 30;

const POSTER_DOTS = Array.from({ length: 144 }, (_, index) => {
    const vertical = 1 - 2 * (index + 0.5) / 144;
    const radius = Math.sqrt(1 - vertical * vertical);
    const angle = index * Math.PI * (3 - Math.sqrt(5));
    const depth = Math.sin(angle) * radius;
    return {
        x: 160 + Math.cos(angle) * radius * 116,
        y: 160 + vertical * 116,
        depth,
        radius: 1.7 + (depth + 1) * 0.7,
        opacity: 0.25 + (depth + 1) * 0.3,
    };
}).sort((a, b) => a.depth - b.depth);

/** Native-resolution orb geometry, with a finite clock instead of a loading loop. */
export default function PathOrb({ state, source, target }: PathOrbProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const clock = useRef({ state, elapsed: 0 });
    const canvasId = useId();
    const { resolvedMode } = useTheme();
    const reduced = useReducedMotion();
    const [paused, setPaused] = useState(false);
    const [complete, setComplete] = useState(false);
    const [replay, setReplay] = useState(0);
    const [rendering, setRendering] = useState<'pending' | 'ready' | 'unavailable'>('pending');

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        if (clock.current.state !== state) {
            clock.current = { state, elapsed: 0 };
            setComplete(false);
        }

        let ctx: CanvasRenderingContext2D | null;
        try {
            ctx = canvas.getContext('2d');
        } catch (error) {
            console.error('Could not open the topic orb preview.', error);
            setRendering('unavailable');
            return;
        }
        if (!ctx) {
            setRendering('unavailable');
            return;
        }
        const context = ctx;
        const preset = resolvePreset(state, 64);
        let size = 0;
        let dpr = 1;
        let raf = 0;
        let previous: number | null = null;
        let lastPaint = -Infinity;
        let inView = false;
        let painted = false;

        const draw = () => {
            if (!size) return;
            context.setTransform(dpr, 0, 0, dpr, 0, 0);
            context.clearRect(0, 0, size, size);
            const time = reduced ? 0.6 : 0.6 + clock.current.elapsed / 1000 * preset.speed * 0.42;
            MODE_DRAWS[preset.mode](context, size, time, resolvedMode === 'dark', preset.opts);
            if (!painted) {
                painted = true;
                setRendering('ready');
            }
        };
        const resize = () => {
            size = canvas.getBoundingClientRect().width;
            dpr = Math.min(window.devicePixelRatio || 1, 2);
            canvas.width = Math.round(size * dpr);
            canvas.height = Math.round(size * dpr);
            draw();
        };
        const stop = () => {
            cancelAnimationFrame(raf);
            raf = 0;
            previous = null;
        };
        const frame = (now: number) => {
            raf = 0;
            if (previous !== null) clock.current.elapsed = Math.min(DURATION, clock.current.elapsed + now - previous);
            previous = now;
            if (now - lastPaint >= FRAME_INTERVAL || clock.current.elapsed === DURATION) {
                draw();
                lastPaint = now;
            }
            if (clock.current.elapsed === DURATION) {
                setComplete(true);
                previous = null;
                return;
            }
            raf = requestAnimationFrame(frame);
        };
        const start = () => {
            if (raf || paused || reduced || !inView || document.hidden || clock.current.elapsed >= DURATION) return;
            previous = null;
            raf = requestAnimationFrame(frame);
        };
        const sync = () => {
            if (inView && !document.hidden) start();
            else stop();
        };

        resize();
        const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize);
        resizeObserver?.observe(canvas);
        if (!resizeObserver) window.addEventListener('resize', resize);
        const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(entries => {
            inView = entries.some(entry => entry.isIntersecting);
            sync();
        });
        if (observer) observer.observe(canvas);
        else {
            inView = true;
            sync();
        }
        document.addEventListener('visibilitychange', sync);

        return () => {
            stop();
            observer?.disconnect();
            resizeObserver?.disconnect();
            window.removeEventListener('resize', resize);
            document.removeEventListener('visibilitychange', sync);
        };
    }, [state, paused, replay, reduced, resolvedMode]);

    const toggleMotion = () => {
        if (complete) {
            clock.current.elapsed = 0;
            setComplete(false);
            setPaused(false);
            setReplay(value => value + 1);
        } else {
            setPaused(value => !value);
        }
    };
    const unavailable = rendering === 'unavailable';
    const playback = unavailable ? 'unavailable' : reduced ? 'reduced' : complete ? 'settled' : paused ? 'paused' : 'playing';
    const action = reduced || unavailable ? 'Motion off' : complete ? 'Replay motion' : paused ? 'Resume motion' : 'Pause motion';

    return (
        <div className="path-orb" data-orb-state={state} data-orb-playback={playback}>
            <div className="path-orb__flow"><span>{source}</span><ArrowRight size={18} /><span>{target}</span></div>
            <div className="path-orb__drawing">
                <svg className="path-orb__poster" viewBox="0 0 320 320" aria-hidden="true" focusable="false"
                    style={{ visibility: rendering === 'ready' ? 'hidden' : 'visible' }}>
                    {POSTER_DOTS.map((dot, index) => (
                        <circle key={index} cx={dot.x} cy={dot.y} r={dot.radius} fill="currentColor" opacity={dot.opacity} />
                    ))}
                </svg>
                <canvas id={canvasId} ref={canvasRef} className="path-orb__canvas" role="presentation" aria-hidden="true"
                    hidden={unavailable} />
            </div>
            <div className="path-orb__controls">
                <span>{unavailable ? 'Static preview' : reduced ? 'Reduced motion' : complete ? 'Motion settled' : paused ? 'Motion paused' : 'Motion preview'}</span>
                <button type="button" onClick={toggleMotion} aria-controls={canvasId} disabled={reduced || unavailable}
                    title={reduced ? 'Motion is off because reduced motion is enabled' : unavailable ? 'The animated preview is unavailable' : undefined}>
                    {action}
                </button>
            </div>
            {unavailable && <p className="path-orb__notice" role="status">Animated preview unavailable. The topic links still work.</p>}
        </div>
    );
}
